import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

// API SERVICE
import AuthService from "../../store/services/auth.service";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { red } from "@mui/material/colors";

// APP COMPONENTS
import LoadingBox from "../indicators/LoadingBox";

// ICONS
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

function ResetPassword() {
  const history = useHistory();
  const location = useLocation();

  // Extract token from URL query params
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  // State
  const [tokenValid, setTokenValid] = useState(null); // null = loading, true = valid, false = invalid
  const [tokenError, setTokenError] = useState("");
  const [newPassword1, setNewPassword1] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState([]);
  const [passwordError, setPasswordError] = useState("");

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        setTokenError("Link de redefinição de senha inválido.");
        return;
      }

      try {
        const response = await AuthService.validateResetToken(token);

        if (response.data.valid) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setTokenError(
            response.data.error || "Link de redefinição de senha inválido."
          );
        }
      } catch (error) {
        console.error(error);
        setTokenValid(false);

        // Extract error message from response
        const errorMsg =
          error.response?.data?.error ||
          error.response?.data?.detail ||
          error.response?.data?.non_field_errors?.[0] ||
          "Link de redefinição de senha inválido.";

        setTokenError(errorMsg);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage([]);
    setPasswordError("");

    // Validate passwords match
    if (newPassword1 !== newPassword2) {
      setPasswordError("As senhas não correspondem.");
      return;
    }

    // Validate password not empty
    if (!newPassword1 || !newPassword2) {
      setPasswordError("Por favor, preencha todos os campos.");
      return;
    }

    setLoading(true);

    try {
      await AuthService.confirmPasswordReset(token, newPassword1, newPassword2);

      // Success - redirect to login with success parameter
      history.push("/login?reset=success");
    } catch (error) {
      console.error(error);
      setLoading(false);

      // Handle different error types
      if (error.response?.data) {
        const data = error.response.data;

        // Field-specific errors
        if (data.new_password1) {
          setErrorMessage(data.new_password1);
        } else if (data.new_password2) {
          setErrorMessage(data.new_password2);
        } else if (data.non_field_errors) {
          setErrorMessage(data.non_field_errors);
        } else if (data.detail) {
          setErrorMessage([data.detail]);
        } else {
          setErrorMessage(["Erro ao redefinir senha. Tente novamente."]);
        }
      } else {
        setErrorMessage(["Erro ao processar solicitação. Tente novamente."]);
      }
    }
  };

  const handleRequestNewReset = () => {
    history.push("/forgot-password");
  };

  const handleBackToLogin = () => {
    history.push("/login");
  };

  // Loading state while validating token
  if (tokenValid === null) {
    return (
      <Box
        sx={{
          m: "auto",
          width: { xs: "90%", sm: "60%", md: "40%" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 8,
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "primary.main" }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
          Redefinir Senha
        </Typography>
        <LoadingBox heightMetric={"100px"} />
        <Typography align="center" variant="body2" sx={{ mt: 2 }}>
          Validando link de redefinição...
        </Typography>
      </Box>
    );
  }

  // Invalid token state
  if (tokenValid === false) {
    return (
      <Box
        sx={{
          m: "auto",
          width: { xs: "90%", sm: "60%", md: "40%" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 8,
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "error.main" }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
          Link Inválido
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: red[800], mb: 3, textAlign: "center" }}
        >
          {tokenError}
        </Typography>
        <Button
          fullWidth
          variant="contained"
          onClick={handleRequestNewReset}
          sx={{ mb: 2 }}
        >
          Solicitar Novo Link
        </Button>
        <Link
          component="button"
          variant="body2"
          onClick={handleBackToLogin}
          sx={{ mt: 2 }}
        >
          Voltar ao Login
        </Link>
      </Box>
    );
  }

  // Valid token - show password reset form
  return (
    <Box
      sx={{
        m: "auto",
        width: { xs: "90%", sm: "60%", md: "40%" },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mt: 8,
      }}
    >
      <Avatar sx={{ m: 1, bgcolor: "primary.main" }}>
        <LockOutlinedIcon />
      </Avatar>
      <Typography component="h1" variant="h5">
        Redefinir Senha
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 3, textAlign: "center" }}>
        Digite sua nova senha abaixo.
      </Typography>

      <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, width: "100%" }}>
        <TextField
          error={!!passwordError}
          margin="normal"
          required
          fullWidth
          name="newPassword1"
          label="Nova Senha"
          type={showPassword1 ? "text" : "password"}
          id="newPassword1"
          autoFocus
          value={newPassword1}
          onChange={(e) => setNewPassword1(e.target.value)}
          helperText={passwordError}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword1(!showPassword1)}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                >
                  {showPassword1 ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          error={!!passwordError}
          margin="normal"
          required
          fullWidth
          name="newPassword2"
          label="Confirmar Nova Senha"
          type={showPassword2 ? "text" : "password"}
          id="newPassword2"
          value={newPassword2}
          onChange={(e) => setNewPassword2(e.target.value)}
          helperText={passwordError}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={() => setShowPassword2(!showPassword2)}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                >
                  {showPassword2 ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading}
        >
          {loading ? "Processando..." : "Redefinir Senha"}
        </Button>

        {/* Error Messages */}
        {errorMessage.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {errorMessage.map((message, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{ color: red[800], mb: 1 }}
              >
                {message}
              </Typography>
            ))}
          </Box>
        )}

        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Link
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              handleBackToLogin();
            }}
          >
            Voltar ao Login
          </Link>
        </Box>
      </Box>
    </Box>
  );
}

export default ResetPassword;
